import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { isChampvaProviderClaim } from '~/platform/shared/claims-appeals';
import { toggleValues } from '@department-of-veterans-affairs/platform-site-wide/selectors';
import FEATURE_FLAG_NAMES from '@department-of-veterans-affairs/platform-utilities/featureFlagNames';
import AddFilesForm from './AddFilesForm';
import Notification from '../Notification';

import {
  setPageFocus,
  focusNotificationAlert,
  openMailFaxDetails,
} from '../../utils/page';
import {
  submitFiles,
  cancelUpload,
  getClaim as getClaimAction,
  resetUploads,
  clearAdditionalEvidenceNotification,
} from '../../actions';
import { isClaimOpen } from '../../utils/helpers';
import withRouter from '../../utils/withRouter';
import { cstMultiClaimProvider } from '../../selectors';
import { claimTabPath } from '../../utils/claim-tab-helpers';

const STEP_ONE_CLOSED_UPLOADER_FALLBACK_ALERT =
  'We’re currently reviewing your application. You won’t be able to upload any documents unless we ask you for more information.';
const CLOSED_UPLOADER_DESCRIPTION =
  'If we mail you a letter asking for more information, you’ll be able to upload certain supporting documents here. You can also review files you’ve submitted using this tool.';
const DOCUMENTS_REQUESTED_ALERT_FALLBACK =
  'We mailed a letter asking for supporting documents. Read the letter before you submit files using this tool.';

const applicantDisplayName = applicant =>
  [applicant?.firstName, applicant?.lastName].filter(Boolean).join(' ').trim();

// Uploader applicant options come from the backend's shared applicant list
// (cstChampvaFilesApplicants), not from names scraped out of rendered Status
// content. Only applicants the backend says have an open document request are
// selectable, so a sibling who hasn't been asked for anything can't be submitted
// through this form.
//
// The key is omitted rather than sent empty when there's nothing to show, so the
// `|| []` is load-bearing. An entry with no id is dropped: the id is the value we
// submit, and a radio option we can't submit is worse than one that isn't offered.
export const getUploadableApplicantOptions = claim =>
  (claim?.attributes?.cstChampvaFilesApplicants || [])
    .filter(
      applicant =>
        applicant?.documentsRequested === true &&
        applicant?.id !== null &&
        applicant?.id !== undefined,
    )
    .map(applicant => ({
      id: String(applicant.id),
      name: applicantDisplayName(applicant),
    }))
    // An applicant with no name is dropped for the same reason as one with no
    // id: the option is unusable. Names come from MPI, and that lookup is
    // allowed to fail without stopping eligibility -- champva_eligibility_service
    // skips the record on a blank result -- so the shared list can carry an
    // eligible id with no name at all. Offering it renders an unlabelled radio
    // and invites the user to attribute a document to someone they cannot
    // identify, which is worse than not offering it.
    .filter(option => option.name !== '');

class AdditionalEvidencePage extends React.Component {
  componentDidMount() {
    this.props.resetUploads();
    if (this.props.location?.state?.focusMailFaxDetails) {
      setTimeout(() => {
        openMailFaxDetails();
      }, 300);
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(props) {
    if (props.uploadComplete) {
      this.goToFilesPage();
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.props.loading && prevProps.loading) {
      setPageFocus();
    }
  }

  componentWillUnmount() {
    if (!this.props.uploadComplete) {
      this.props.clearAdditionalEvidenceNotification();
    }
  }

  onSubmitFiles(claimId, files) {
    const uploadMetadata = {
      ...this.props.claim?.attributes?.uploadMetadata,
      provider: this.props.claim?.attributes?.provider,
      // eslint-disable-next-line camelcase
      champva_cst_file_uploader_docs_only_resubmission:
        this.props.champvaCstFileUploaderDocsOnlyResubmission,
    };

    this.props.submitFiles(claimId, null, files, uploadMetadata);
  }

  goToFilesPage() {
    const providerSearch = this.props.location?.search || '';
    const filesPath = this.props.useVaTabs
      ? claimTabPath(this.props.claim.id, 'files', { search: providerSearch })
      : `../files${providerSearch}`;
    const provider = this.props.cstMultiClaimProviderEnabled
      ? this.props.claim?.attributes?.provider
      : null;
    this.props.getClaim(this.props.claim.id, null, provider);
    // Flag-on this is a same-URL refresh return; replace so browser back
    // still leaves the claim instead of landing on a duplicate entry.
    if (this.props.useVaTabs) {
      this.props.navigate(filesPath, { replace: true });
    } else {
      this.props.navigate(filesPath);
    }
  }

  render() {
    const { claim } = this.props;

    let content;

    const isClaimCurrentlyOpen = isClaimOpen(
      claim.attributes.status,
      claim.attributes.closeDate,
    );
    const isOpen =
      typeof this.props.forceUploaderOpen === 'boolean'
        ? this.props.forceUploaderOpen
        : isClaimCurrentlyOpen;

    if (this.props.loading) {
      content = (
        <va-loading-indicator
          set-focus
          message="Loading your claim information..."
        />
      );
    } else {
      const { message } = this.props;
      const isChampvaFlow =
        this.props.champvaCustomContentEnabled && isChampvaProviderClaim(claim);
      const filesMeta = claim?.attributes?.claimStatusMeta?.files;
      const isChampvaUploaderClosed = isChampvaFlow && isOpen === false;
      const isStepOneUploaderClosed =
        isChampvaFlow && isClaimCurrentlyOpen && isOpen === false;
      // forceUploaderOpen is true whenever an applicant has documentsRequested
      // (see FilesPage's hasAnyRequestedDocuments), in Step 1 or Step 2 --
      // that's the same trigger as the "mailed a letter" copy, so the alert
      // applies either way, not just once the claim is fully decided.
      const showDocumentsRequestedAlert =
        isChampvaFlow && this.props.forceUploaderOpen === true;
      const stepOneClosedAlertText =
        filesMeta?.stepOneClosedAlertText ||
        STEP_ONE_CLOSED_UPLOADER_FALLBACK_ALERT;
      const documentsRequestedAlertText =
        filesMeta?.documentsRequestedAlertText ||
        DOCUMENTS_REQUESTED_ALERT_FALLBACK;
      const champvaClosedAlertText = isStepOneUploaderClosed
        ? stepOneClosedAlertText
        : filesMeta?.closedAlertText;
      const champvaClosedAlertPrefix = isStepOneUploaderClosed
        ? null
        : filesMeta?.closedAlertPrefix;
      const filesDescription = isChampvaUploaderClosed
        ? filesMeta?.closedUploaderDescription || CLOSED_UPLOADER_DESCRIPTION
        : filesMeta?.description;
      const applicantOptions = isChampvaFlow
        ? getUploadableApplicantOptions(claim)
        : [];
      // Without the shared applicant list there is nothing to attribute an
      // upload to, and the "Select an applicant" validation is itself gated on
      // there being options -- so rendering the uploader anyway would let a
      // document be filed against nobody with no error shown. Hide it instead.
      //
      // Scoped to isOpen on purpose. A legitimately closed uploader also has no
      // options, but it already explains itself through the closed alert and
      // closedUploaderDescription, and that messaging must survive.
      //
      // The documents-requested alert above still renders either way: a letter
      // really was mailed, and that stays true whether or not we can accept the
      // response here. In practice this only fires before the backend's shared
      // applicant list ships, since both it and cstChampvaApplicants come from
      // the same eligibility summary and are consistent once present.
      const champvaUploaderUnavailable =
        isChampvaFlow && isOpen && applicantOptions.length === 0;

      content = (
        <div className="additional-evidence-container">
          {message && (
            <Notification
              title={message.title}
              body={message.body}
              type={message.type}
              maskTitle={message.type === 'error'}
              onSetFocus={focusNotificationAlert}
            />
          )}
          {isChampvaUploaderClosed && champvaClosedAlertText && (
            <va-alert status="info" visible class="vads-u-margin-y--4">
              <p className="vads-u-margin-y--0">
                {champvaClosedAlertPrefix && (
                  <>
                    <strong>{champvaClosedAlertPrefix}</strong>{' '}
                  </>
                )}
                {champvaClosedAlertText}
              </p>
            </va-alert>
          )}
          {showDocumentsRequestedAlert && (
            <va-alert status="warning" visible class="vads-u-margin-y--4">
              <p className="vads-u-margin-y--0">
                {documentsRequestedAlertText}
              </p>
            </va-alert>
          )}
          {/* Hidden alongside the uploader when champvaUploaderUnavailable:
              this copy says "you can upload your supporting documents here",
              which is not true once the uploader is hidden. The alert above
              still tells the user a letter was mailed. */}
          {isChampvaFlow && !champvaUploaderUnavailable && filesDescription && (
            <>
              <p className="vads-u-margin-top--1 vads-u-margin-bottom--4">
                {filesDescription}
              </p>
              {filesMeta?.ohiDescription && (
                <p className="vads-u-margin-top--1 vads-u-margin-bottom--4">
                  {filesMeta.ohiDescription}
                </p>
              )}
              {filesMeta?.ohiLink && (
                <p className="vads-u-margin-top--0 vads-u-margin-bottom--4">
                  <va-link
                    href={filesMeta.ohiLink.href}
                    text={filesMeta.ohiLink.text}
                  />
                </p>
              )}
            </>
          )}
          {isOpen && !champvaUploaderUnavailable ? (
            <>
              {!isChampvaFlow && (
                <h3 id="add-files" className="vads-u-margin-bottom--3">
                  {this.props.additionalEvidenceTitle || 'Additional evidence'}
                </h3>
              )}
              {isChampvaFlow && filesMeta?.uploadTitle && (
                <>
                  <h3 className="vads-u-margin-bottom--1">
                    {filesMeta.uploadTitle}
                  </h3>
                  {filesMeta?.uploadDescription && (
                    <p className="vads-u-margin-top--0 vads-u-margin-bottom--3">
                      {filesMeta.uploadDescription}
                    </p>
                  )}
                </>
              )}
              <AddFilesForm
                useVaTabs={this.props.useVaTabs}
                acceptedFileTypes={
                  claim?.attributes?.uploadMetadata?.acceptedFileTypes
                }
                applicantOptions={applicantOptions}
                documentTypes={
                  claim?.attributes?.uploadMetadata?.documentTypeOptions
                }
                isChampvaFlow={isChampvaFlow}
                fileTab
                hideOtherWaysLink={this.props.hideOtherWaysLink}
                progress={this.props.progress}
                uploadResults={this.props.uploadResults}
                uploading={this.props.uploading}
                onCancel={this.props.cancelUpload}
                onSubmit={files => this.onSubmitFiles(claim.id, files)}
              />
            </>
          ) : (
            !isChampvaFlow && (
              <p className="vads-u-margin-top--0 vads-u-margin-bottom--4">
                The claim is closed so you can no longer submit any additional
                evidence.
              </p>
            )
          )}
        </div>
      );
    }

    return (
      <>
        <div name="topScrollElement" />
        {content}
      </>
    );
  }
}

function mapStateToProps(state) {
  const claimsState = state.disability.status;
  const claim = claimsState.claimDetail.detail;
  return {
    loading: claimsState.claimDetail.loading,
    claim,
    uploadResults: claimsState.uploads.uploadResults,
    uploading: claimsState.uploads.uploading,
    progress: claimsState.uploads.progress,
    uploadComplete: claimsState.uploads.uploadComplete,
    message: claimsState.notifications.additionalEvidenceMessage,
    champvaCstFileUploaderDocsOnlyResubmission:
      state.featureToggles?.champva_cst_file_uploader_docs_only_resubmission ||
      false,
    champvaCustomContentEnabled:
      state.featureToggles?.cst_champva_custom_content || false,
    cstMultiClaimProviderEnabled: cstMultiClaimProvider(state),
    useVaTabs: toggleValues(state)[FEATURE_FLAG_NAMES.cstClaimDetailsVaTabs],
  };
}

const mapDispatchToProps = {
  submitFiles,
  cancelUpload,
  getClaim: getClaimAction,
  resetUploads,
  clearAdditionalEvidenceNotification,
};

AdditionalEvidencePage.propTypes = {
  additionalEvidenceTitle: PropTypes.string,
  cancelUpload: PropTypes.func,
  champvaCstFileUploaderDocsOnlyResubmission: PropTypes.bool,
  champvaCustomContentEnabled: PropTypes.bool,
  claim: PropTypes.object,
  clearAdditionalEvidenceNotification: PropTypes.func,
  cstMultiClaimProviderEnabled: PropTypes.bool,
  forceUploaderOpen: PropTypes.bool,
  getClaim: PropTypes.func,
  hideOtherWaysLink: PropTypes.bool,
  loading: PropTypes.bool,
  location: PropTypes.object,
  message: PropTypes.object,
  navigate: PropTypes.func,
  params: PropTypes.object,
  progress: PropTypes.number,
  resetUploads: PropTypes.func,
  submitFiles: PropTypes.func,
  uploadComplete: PropTypes.bool,
  uploadResults: PropTypes.object,
  uploading: PropTypes.bool,
  useVaTabs: PropTypes.bool,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AdditionalEvidencePage),
);

export { AdditionalEvidencePage };
